import type { CreateJobBody } from '@/api/opsTypes';
import { parseDisplayedCredits } from './credits';

/**
 * The create-tenant wizard's rules, as pure functions (unit-tested). They MIRROR the API's validation
 * (docs/api-tenant-ops.md §4.2) so the admin learns about a bad value on the step that owns it — the API
 * still re-validates everything.
 */

/** `db_short` becomes `iam_t_<db_short>` and can never be renamed: 2–32, lowercase letter first, [a-z0-9]. */
export const DB_SHORT_PATTERN = /^[a-z][a-z0-9]{1,31}$/;

export function dbShortError(value: string): string | null {
  const v = value.trim();
  if (v.length === 0) return 'Required — it names the tenant database (iam_t_<db_short>).';
  if (v !== v.toLowerCase()) return 'Lowercase only.';
  if (!/^[a-z]/.test(v)) return 'Must start with a letter.';
  if (/[^a-z0-9]/.test(v)) return 'Letters and digits only — no dashes, underscores or spaces.';
  if (v.length < 2) return 'At least 2 characters.';
  if (v.length > 32) return 'At most 32 characters.';
  return DB_SHORT_PATTERN.test(v) ? null : 'Invalid db_short.';
}

/** A db_short proposal from a label: "Asten College" → "astencollege". The admin can still edit it. */
export function suggestDbShort(label: string): string {
  const letters = label
    .normalize('NFKD')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');
  const trimmed = letters.replace(/^[0-9]+/, '');
  return trimmed.slice(0, 32);
}

export function labelError(label: string): string | null {
  const v = label.trim();
  if (v.length < 2) return 'At least 2 characters.';
  if (v.length > 100) return 'At most 100 characters (it becomes the chat brand and widget name).';
  return null;
}

export function emailError(email: string): string | null {
  const v = email.trim();
  const at = v.indexOf('@');
  if (v.length < 3 || v.length > 320 || at <= 0 || at === v.length - 1 || /\s/.test(v)) return 'A valid e-mail address.';
  return null;
}

/** The API's host rule: host names only — no scheme, port or path. */
export const HOST_PATTERN = /^(?=.{1,253}$)([a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?)(\.[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?)+$/;

export function hostError(host: string): string | null {
  const v = host.trim().toLowerCase();
  if (!v) return 'Empty host.';
  if (/^[a-z]+:\/\//.test(v)) return `${host}: no scheme — just the host name.`;
  if (v.includes('/') || v.includes(':')) return `${host}: no port or path.`;
  return HOST_PATTERN.test(v) ? null : `${host}: not a host name.`;
}

export const PLAN_CODE_PATTERN = /^[a-z][a-z0-9_]{0,63}$/;

export interface WizardState {
  environment: string;
  kind: 'tavi' | 'external';
  label: string;
  dbShort: string;
  useType: 'team' | 'personal';
  founderEmail: string;
  founderFirstName: string;
  founderLastName: string;
  generatePassword: boolean;
  industryPack: string;
  /** "Add sample records" — a few fictional records so the owner sees the CRM working. On by default. */
  sampleRecords: boolean;
  planCode: string;
  apps: string[];
  hosts: string[];
  /** DISPLAYED credits, as typed; converted with billing's scale before sending (§4.5). */
  aiCredits: string;
  messagingCredits: string;
  grantReason: string;
}

export const INITIAL_WIZARD: WizardState = {
  environment: '',
  kind: 'tavi',
  label: '',
  dbShort: '',
  useType: 'team',
  founderEmail: '',
  founderFirstName: '',
  founderLastName: '',
  generatePassword: false,
  industryPack: 'general',
  sampleRecords: true,
  planCode: '',
  apps: [],
  hosts: [],
  aiCredits: '',
  messagingCredits: '',
  grantReason: '',
};

export type WizardStep = 'environment' | 'kind' | 'identity' | 'founder' | 'pack' | 'plan' | 'apps' | 'hosts' | 'grants' | 'review';

/** An external account has no founder, no pack, no apps choice (forced messaging + billing) and no hosts. */
export function stepsFor(kind: WizardState['kind']): WizardStep[] {
  return kind === 'external'
    ? ['environment', 'kind', 'identity', 'plan', 'grants', 'review']
    : ['environment', 'kind', 'identity', 'founder', 'pack', 'plan', 'apps', 'hosts', 'grants', 'review'];
}

/** Converted grant overrides, or why they cannot be converted. Empty fields send nothing. */
export function grantOverrides(
  state: WizardState,
  scale: number | null,
): { ok: true; value: { ai_credits?: number; messaging_credits?: number } | undefined } | { ok: false; error: string } {
  const out: { ai_credits?: number; messaging_credits?: number } = {};
  const fields: [keyof typeof out, string][] = [
    ['ai_credits', state.kind === 'external' ? '' : state.aiCredits],
    ['messaging_credits', state.messagingCredits],
  ];
  for (const [name, text] of fields) {
    if (!text.trim()) continue;
    const parsed = parseDisplayedCredits(text, scale);
    if (!parsed.ok) return { ok: false, error: `${name === 'ai_credits' ? 'AI' : 'Messaging'} credits: ${parsed.error}` };
    out[name] = parsed.micro;
  }
  return { ok: true, value: Object.keys(out).length ? out : undefined };
}

/** Why a step cannot be left yet, or null. */
export function stepError(step: WizardStep, state: WizardState, scale: number | null): string | null {
  switch (step) {
    case 'environment':
      return state.environment ? null : 'Choose an environment.';
    case 'identity':
      return labelError(state.label) ?? dbShortError(state.dbShort);
    case 'founder':
      return emailError(state.founderEmail);
    case 'pack':
      return state.industryPack ? null : 'Choose an industry pack (or "none").';
    case 'plan':
      return PLAN_CODE_PATTERN.test(state.planCode.trim().toLowerCase()) ? null : 'Choose or type a plan code.';
    case 'apps':
      return state.apps.length > 0 ? null : 'At least one app.';
    case 'hosts': {
      if (state.hosts.length > 20) return 'At most 20 hosts.';
      for (const h of state.hosts) {
        const e = hostError(h);
        if (e) return e;
      }
      return null;
    }
    case 'grants': {
      const grants = grantOverrides(state, scale);
      if (!grants.ok) return grants.error;
      const any = Object.values(grants.value ?? {}).some((v) => (v ?? 0) > 0);
      if (any && state.grantReason.trim().length < 3) return 'A grant reason is required when an override is above zero.';
      return null;
    }
    default:
      return null;
  }
}

/** The request body for POST /provisioning/jobs. Micro-credits only; displayed figures never leave. */
export function buildJobBody(state: WizardState, scale: number | null): CreateJobBody {
  const grants = grantOverrides(state, scale);
  const overrides = grants.ok ? grants.value : undefined;
  const base: CreateJobBody = {
    environment: state.environment,
    kind: state.kind,
    label: state.label.trim(),
    db_short: state.dbShort.trim(),
    plan_code: state.planCode.trim().toLowerCase(),
  };
  if (overrides) {
    base.grant_overrides = overrides;
    if (state.grantReason.trim()) base.grant_reason = state.grantReason.trim();
  }
  if (state.kind === 'external') return base;

  return {
    ...base,
    use_type: state.useType,
    founder_email: state.founderEmail.trim(),
    founder_first_name: state.founderFirstName.trim() || undefined,
    founder_last_name: state.founderLastName.trim() || undefined,
    generate_password: state.generatePassword,
    industry_pack: state.industryPack,
    ...(state.industryPack !== 'none' ? { sample_records: state.sampleRecords } : {}),
    apps: state.apps,
    hosts: state.hosts.map((h) => h.trim().toLowerCase()),
  };
}

/** The create form's fields (2026-09-30 one-page form). `advanced` = lives under "Advanced". */
export type FormField = 'label' | 'founderEmail' | 'industryPack' | 'planCode' | 'environment' | 'dbShort' | 'apps' | 'hosts' | 'grants';

export const ADVANCED_FIELDS: ReadonlySet<FormField> = new Set(['environment', 'dbShort', 'apps', 'hosts', 'grants']);

/** Every problem on the form, by field, in the order the form shows them. The wizard steps' rules, unchanged. */
export function formProblems(state: WizardState, scale: number | null): { field: FormField; message: string }[] {
  const out: { field: FormField; message: string }[] = [];
  const push = (field: FormField, message: string | null) => { if (message) out.push({ field, message }); };
  push('label', labelError(state.label));
  if (state.kind === 'tavi') {
    push('founderEmail', emailError(state.founderEmail));
    push('industryPack', stepError('pack', state, scale));
  }
  push('planCode', stepError('plan', state, scale));
  push('environment', stepError('environment', state, scale));
  push('dbShort', dbShortError(state.dbShort));
  if (state.kind === 'tavi') {
    push('apps', stepError('apps', state, scale));
    push('hosts', stepError('hosts', state, scale));
  }
  push('grants', stepError('grants', state, scale));
  return out;
}

/** What the form will do, in plain sentences — the review the admin reads before creating. */
export function setupSummary(
  state: WizardState,
  names: { environment?: string | null; pack?: string | null; packParts?: string[]; plan?: string | null; scale?: number | null },
): string[] {
  const label = state.label.trim() || 'the tenant';
  const env = names.environment || state.environment || 'the chosen environment';
  const lines = [
    state.kind === 'external'
      ? `Create “${label}” on ${env} as an external account for SMS/WhatsApp and payments (no owner login, no industry content).`
      : `Create “${label}” on ${env}.`,
    `Its database will be named iam_t_${state.dbShort.trim() || '…'} — this name can never be changed.`,
  ];
  if (state.kind === 'tavi') {
    const email = state.founderEmail.trim() || 'the owner';
    lines.push(state.generatePassword
      ? `Generate a password for ${email}; you reveal it once on the next page and hand it over yourself.`
      : `E-mail ${email} an invitation to set their own password.`);
    if (state.industryPack === 'none') {
      lines.push('Start from the plain platform set-up — no industry content.');
    } else {
      const parts = names.packParts?.length ? `: ${names.packParts.join(', ')}` : '';
      lines.push(`Add the ${names.pack ?? state.industryPack} industry content${parts}${state.sampleRecords ? ', plus a few sample records' : ''}.`);
    }
  }
  const plan = names.plan || state.planCode.trim();
  const onPlan = plan ? `Put it on the ${plan} plan` : 'Put it on the plan you choose';
  lines.push(state.kind === 'external'
    ? `${onPlan} with messaging and billing.`
    : `${onPlan} with ${state.apps.length ? state.apps.join(', ') : 'the apps you choose'}.`);
  if (state.kind === 'tavi') {
    lines.push(state.hosts.length
      ? `Register the web address${state.hosts.length === 1 ? '' : 'es'} ${state.hosts.join(', ')}.`
      : 'No own web address — people sign in on the main address.');
  }
  const grants = grantOverrides(state, names.scale ?? null);
  // The credits steps: AI from the plan, SMS/WhatsApp from the industry — an external account or "No industry" has
  // no industry grant, so it starts with none unless an amount is typed.
  lines.push(grants.ok && grants.value
    ? `Give the starting credits typed under Advanced (reason: “${state.grantReason.trim()}”).`
    : state.kind === 'external'
      ? 'It starts with no SMS/WhatsApp credits unless you type an amount under Advanced.'
      : state.industryPack === 'none'
        ? "Give the plan's starting AI credits (no SMS/WhatsApp credits without an industry, unless typed under Advanced)."
        : "Give the plan's starting AI credits and the industry's starting SMS/WhatsApp credits.");
  // The welcome e-mail is sent only for a generated password; an invited owner's invitation IS their welcome.
  lines.push(state.kind === 'external'
    ? 'Then check every service.'
    : state.generatePassword
      ? 'Then check every service and e-mail the owner a welcome message with the sign-in address.'
      : 'Then check every service. The invitation e-mail is the owner’s welcome.');
  return lines;
}
